import * as React from "react"
import {
  type Transaction,
  useDeleteTransactionsMutation,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgTrash01 } from "@digits-shared/components/SVGIcons/line/Trash01.svg"
import { SvgAlertTriangleSolid } from "@digits-shared/components/SVGIcons/solid/AlertTriangleSolid.svg"
import { SvgTrash01Solid } from "@digits-shared/components/SVGIcons/solid/Trash01Solid.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { AlertModal } from "@digits-shared/DesignSystem/Modals/AlertModal"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import { defined } from "@digits-shared/helpers/filters"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import { v4 as generateUUID } from "uuid"
import { useHasTransactionsInClosedPeriod } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/hooks/useHasTransactionsInClosedPeriod"
import { useIsBulkDisabled } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/hooks/useIsBulkDisabled"
import { useChromeAlertContext } from "src/frontend/components/Shared/Alerts/ChromeAlertsContext"
import type FrontendSession from "src/frontend/session"

export const BulkDeleteButton: React.FC<{
  transactions: Transaction[]
  disabled?: boolean
}> = ({ transactions, disabled }) => {
  const { currentLegalEntityId } = useSession<FrontendSession>()
  const { sourceString, areTransactionsDeletable } = useIsBulkDisabled(transactions)
  const hasTransactionsInClosedPeriod = useHasTransactionsInClosedPeriod(transactions)
  const [deleteTransactions] = useDeleteTransactionsMutation()
  const { addAlert } = useChromeAlertContext()

  const {
    value: isDeleteConfirmationVisible,
    setTrue: showDeleteConfirmation,
    setFalse: closeDeleteConfirmation,
  } = useStateBoolean(false)

  const {
    value: isClosedPeriodWarningVisible,
    setTrue: showClosedPeriodWarning,
    setFalse: hideClosedPeriodWarning,
  } = useStateBoolean(false)

  const bulkDeleteTransactions = React.useCallback(() => {
    const factIds = transactions.map((t) => t.factId).filter(defined)
    if (factIds.length === 0) return
    return deleteTransactions({
      variables: {
        factIds,
        legalEntityId: currentLegalEntityId,
      },
    })
      .then(() => {
        closeDeleteConfirmation()
        hideClosedPeriodWarning()
        addAlert({
          id: generateUUID(),
          message: `${factIds.length} ${stringHelper.pluralize(factIds.length, "transaction", "transactions")} deleted`,
          autoDismiss: true,
          SVGComponent: SvgTrash01Solid,
        })
      })
      .catch(() => {
        addAlert({
          id: generateUUID(),
          message: "Error deleting transactions",
          autoDismiss: true,
          SVGComponent: SvgAlertTriangleSolid,
        })
      })
  }, [
    addAlert,
    closeDeleteConfirmation,
    currentLegalEntityId,
    deleteTransactions,
    hideClosedPeriodWarning,
    transactions,
  ])

  const deleteText = React.useMemo(
    () =>
      `Delete ${transactions.length} ${stringHelper.pluralize(transactions.length, "Transaction", "Transactions")}`,
    [transactions.length]
  )
  return (
    <>
      <DigitsTooltip
        maxWidth={200}
        disabled={areTransactionsDeletable}
        content={
          <div>
            <strong>Delete not available</strong>
            {sourceString && (
              <>
                <br />
                Transactions with the following sources cannot be deleted:
                <br />
                <strong>{sourceString}</strong>
              </>
            )}
          </div>
        }
      >
        <DigitsButton
          onClick={hasTransactionsInClosedPeriod ? showClosedPeriodWarning : showDeleteConfirmation}
          $variant="ghost-dark"
          $circle
          size="medium"
          disabled={!areTransactionsDeletable || disabled}
        >
          <SvgTrash01 />
        </DigitsButton>
      </DigitsTooltip>

      {isDeleteConfirmationVisible && (
        <AlertModal
          headerLabel={deleteText}
          primaryButtonLabel={deleteText}
          primaryButtonClick={bulkDeleteTransactions}
          primaryButtonVariant="error"
          secondaryButtonLabel="Cancel"
          secondaryButtonClick={closeDeleteConfirmation}
        >
          Are you sure you would like to permanently delete the selected transactions?
        </AlertModal>
      )}

      {isClosedPeriodWarningVisible && (
        <AlertModal
          headerLabel="Deleting transactions from a Closed Period"
          primaryButtonLabel={deleteText}
          primaryButtonClick={bulkDeleteTransactions}
          primaryButtonVariant="error"
          secondaryButtonLabel="Cancel"
          secondaryButtonClick={hideClosedPeriodWarning}
        >
          Some of the transactions relate to an already closed period. Proceeding with this action
          will impact closed period balances. <br /> Are you sure you would like to permanently
          deleted the selected transactions?
        </AlertModal>
      )}
    </>
  )
}
